import { Alert, Button, DialogContentText, Typography } from '@mui/material';
import type React from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccount } from '../../contexts/AccountContext';
import { usePreventNavigation } from '../../hooks/usePreventNavigation';
import { apiClient } from '../../services/api';
import TransferProgressMessage from '../common/TransferProgressMessage';
import { BaseModal } from '../common/UI/BaseModal';

interface ApiError extends Error {
  response?: {
    data?: {
      message?: string;
    };
  };
}

interface SharepointOAuthDisconnectProps {
  redirectPath?: string;
}

const SharepointOAuthDisconnect: React.FC<SharepointOAuthDisconnectProps> = ({
  redirectPath = '/account',
}) => {
  const { refreshUserDetails } = useAccount();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  usePreventNavigation(isLoading);

  const handleSwitch = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // 5 minute timeout for large file transfers
      await apiClient.post('/switchToFirebaseStorage', {}, { timeout: 300000 });
      setIsSuccess(true);
    } catch (err: unknown) {
      const error = err as ApiError;
      setError(error.response?.data?.message || error.message || 'An unexpected error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSuccess = async () => {
    await refreshUserDetails();
    setOpen(false);
    navigate(redirectPath);
  };

  return (
    <>
      <Button
        fullWidth
        variant="outlined"
        color="warning"
        onClick={() => setOpen(true)}
        disabled={isLoading}
      >
        Switch to BIDLOGIQ Storage
      </Button>
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <BaseModal
        open={open}
        onClose={() => !isLoading && !isSuccess && setOpen(false)}
        title={
          isSuccess
            ? 'Switch Complete'
            : isLoading
              ? 'Switching Storage...'
              : 'Confirm Storage Change'
        }
        actions={
          isSuccess ? (
            <Button onClick={handleCloseSuccess} color="primary" variant="contained">
              Close
            </Button>
          ) : isLoading ? null : (
            <>
              <Button onClick={() => setOpen(false)} disabled={isLoading}>
                Cancel
              </Button>
              <Button
                onClick={handleSwitch}
                color="warning"
                variant="contained"
                disabled={isLoading}
              >
                Confirm & Switch
              </Button>
            </>
          )
        }
      >
        {isSuccess ? (
          <Alert severity="success" sx={{ mt: 2 }}>
            Storage successfully switched to BIDLOGIQ Storage. Your files have been transferred.
          </Alert>
        ) : isLoading ? (
          <TransferProgressMessage isLoading={isLoading} title="Transferring Files" />
        ) : (
          <>
            <DialogContentText>
              Are you sure you want to switch to BIDLOGIQ Cloud Storage?
            </DialogContentText>
            <Alert severity="info" sx={{ mt: 2 }}>
              <strong>Transfer Process:</strong> Your existing files in SharePoint will be
              automatically transferred to BIDLOGIQ storage. This process may take a few minutes
              depending on the number of files.
            </Alert>
            <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
              Please do not close this window until the process is complete.
            </Typography>
          </>
        )}
      </BaseModal>
    </>
  );
};

export default SharepointOAuthDisconnect;
